import React, { useMemo } from 'react';

import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomVisualInspectionSummary from './custom-utm-summary';
import CustomDamageClass from '../custom-general/custom-damage-class';
import CustomUTMBreakdown from './custom-utm-breakdown';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomUTMReportPDF from './custom-utm-report';
import EquipmentAnalytics from '../custom-visual-inspection/report-items/equipmentAnalytics';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface CustomExxonMobilUTMEquipmentReportProps {
	coverData: models.CustomFVIData;
    reportTitle: string;
    summary: models.CustomUTMEquipmentSummary;
	anomalySummary: models.EquipmentAnomalySummaryItem;
    anomalyItems: models.NewAnomalyItem[];
    pidData: models.PIDData;
	generalDrawing: models.PDFImageProps[];
	photographicData: models.PDFImageProps[];
	projectDetails: models.Project;
	isoImage: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	inspectionOverview: models.CustomUTMOverview;
}

const CustomExxonMobilUTMEquipmentReport: React.FC<CustomExxonMobilUTMEquipmentReportProps> = (props): JSX.Element => {
	const { inspectionTypeName } = props.projectDetails;

    const overview = useCoverSheetOverview();

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

            <CustomInfoSheet
				projectDetails={props.projectDetails}
                workOrderNumber={props.coverData.workOrderNumber}
                reportTitle={props.reportTitle}
            />

            <OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<ExecutiveSummary summary={props.summary.overview} />

			<CustomDamageClass />

            <CustomVisualInspectionSummary anomalyItems={props.anomalyItems} />

			<CustomUTMBreakdown
				corrosionAllowance={props.inspectionOverview.corrosionAllowance}
				inspectionItems={props.utInspectionItems} 
			/>

			<EquipmentAnalytics 
				items={[
					{
						hasPDO: 'no',
						pdoChecklist: [],
						hasPID: props.pidData.hasPID,
						equipmentNumber: props.summary.equipmentNumber,
						anomalySummary: props.anomalySummary, 
						hasAnomalies: props.summary.hasAnomalies, 
						inspectionType: 'internal',
						additionalComment: '',
						checklistItems: [],
						anomalyItems: props.anomalyItems,
						pIDImages: props.pidData.pIDImages,
						pIDNumber: props.pidData.pIDNumber,
						generalPhotos: [],
						generalDrawing: props.generalDrawing,
						id: 1,
						inspectionDescription: 'No anomaly in visual inspection'
					}
				]}
			/>

			<CustomUTMReportPDF
				tmlLocations={props.isoImage}
				utInspectionItems={props.utInspectionItems}
				inspectionOverview={props.inspectionOverview}
			/>
			
			<CustomGeneralPhotos label="Photographic Record" photos={props.photographicData} />
		</React.Fragment>
	);
};

export default withPDF(CustomExxonMobilUTMEquipmentReport);
